// MapController.js
// Verhaltens-/Zustandskern der Karten-Domäne (aus der TopoMapCore-Fassade extrahiert, Phase 2d.2).
// Besitzt mapAdapter-Referenz, Marker-Manager, Clusterer, Kreissuche, Event-Wiring, Map-Listener
// und die Legacy-Connectoren. Die dünne Fassade delegiert hierher.

import { timeoutRegistry, mediator } from "../../mediator/mediatorInstance.js";
import { EventLinker } from "./EventLinker.js";
import { DocumentMarkerManager } from "../marker/mainmap/DocumentMarkerManager.js";
import { ArchiveMarkerManager } from "../marker/mainmap/ArchiveMarkerManager.js";
import { GalleryMarkerManager } from "../marker/gallery/GalleryMarkerManager.js";
import { MarkerClustererAdapter } from "../adapter/MarkerClustererAdapter.js";
import { MapPositions } from "../tools/MapPositions.js";
import { MAP_OPTIONS } from "../tools/MapOptions.js";
import { CircleSearch } from "../tools/CircleSearch.js";
import { createMapUiReconciler } from "../tools/MapUiReconciler.js";
import { MapStateStore, createDefaultMapState } from "../state/MapStateStore.js";

// Slice 4b (Test): true = beim Kontextwechsel die Overlay-Einstellungen (visible/opacity) pro Kontext BEHALTEN (Memory);
// false = Reset (heutiges Verhalten). Wirkt über state.keepOnPositionChange im jeweiligen Kontext-Store.
const KEEP_SETTINGS_ON_SWITCH = true;

const MARKER_MODE = { MAIN: 1, GALLERY: 2, ALL: -99 };

function checklistener(listener, name) {
    return listener.o === name;
}

export class MapController {
    mapAdapter;
    viewController;
    mapPositions;
    mapListeners;
    mapOptions;

    markerClustererLib;
    markerClusterer;
    documentMarkerManager;
    archiveMarkerManager;
    galleryMarkerManager;

    eventLinker;
    circleSearch;
    overlay;
    eventListeners = [];

    // legacy bridge
    layoutConnector;
    globalObjectConnector;
    openGalleryLegacy;

    constructor({ mapAdapter, markerFactory, markerClustererLib, viewController }) {
        this.mapAdapter = mapAdapter;
        this.viewController = viewController;
        this.markerClustererLib = markerClustererLib;
        this.mapPositions = new MapPositions();
        this.mapListeners = new Map();

        this.documentMarkerManager = new DocumentMarkerManager(mapAdapter, markerFactory);
        this.archiveMarkerManager = new ArchiveMarkerManager(mapAdapter, markerFactory);
        this.galleryMarkerManager = new GalleryMarkerManager(mapAdapter, markerFactory);
    }

    // --- Bootstrap / Events / Listener ---

    setOpenGalleryHandler(callback) {
        this.openGalleryLegacy = callback;
    }

    initializeLegacyBridges({ layoutConnector, globalObjectConnector }) {
        this.globalObjectConnector = globalObjectConnector;
        this.layoutConnector = layoutConnector;
    }

    registerEvents() {
        this.eventLinker = new EventLinker(mediator, this.mapAdapter);
        this.eventLinker.registerEvents({
            onArchiveHover: this.highlightArchiveMarker.bind(this),
            onDocumentHover: this.highlightDocumentMarker.bind(this),
            onHoverShowThumbObject: this.globalObjectConnector.GetBasicJSHoverThumbObject.bind(this),
            onDocumentClick: this.openGalleryLegacy.bind(this),
            onMainMapFitToMarkers: this.fitToAllMainMapMarkers.bind(this),
            onGalleryMapFitToMarkers: this.fitToGalleryMapMarkers.bind(this),
        });
    }

    /**
     * Initialisiert die Karte (inkl. Übergabe von mapOptions an den Adapter)
     * @param {string} canvasId - DOM-Element-ID für die Karte
     * @param {object} mapOptions - Optionen wie center, zoom, mapTypeId usw.
     * @param {string} mode - mit welchem Optionspaket die Map initialisiert wird
     */
    initialize(canvasId, mapOptions = {}, mode = 'main') {
        this.mapOptions = mapOptions;
        const opts = { ...MAP_OPTIONS[mode], ...mapOptions };
        this.mapAdapter.initializeMap(canvasId, opts);

        this.circleSearch = new CircleSearch(this.mapAdapter, this.mapAdapter.getMapInstance(), mediator);
        this.circleSearch.setActive();

        this.setupClusterer(this.markerClustererLib);
    }

    /**
     * Vollständiger Map-Bootstrap: Bridges/Events verdrahten, Map initialisieren, Listener + Controls
     * aufsetzen und in den Startmodus (Main/Gallery) gehen. Gibt die Map-Instanz zurück (oder null bei Fehler).
     */
    start({ canvasId, mapOptions, layoutConnector, globalObjectConnector, openGallery, initGallery = false }) {
        this.initializeLegacyBridges({ layoutConnector, globalObjectConnector });
        this.viewController.setConnectors({ layoutConnector, globalObjectConnector });
        this.setOpenGalleryHandler(openGallery);
        this.registerEvents();
        this.subscribeLegacyEvents();

        try {
            this.initialize(canvasId, mapOptions);
        } catch (error) {
            console.error('[MapController] start() failed during map init: ' + error);
            return null;
        }

        if (!initGallery && this.layoutConnector.GetGalleryMapWrapper()) {
            const galleryResult = this.globalObjectConnector.GetIndexJSGlobalGalleryResultObject();
            if (galleryResult) {
                galleryResult.addMarkers = false;
            }
        }

        $(window).on('scroll', () => this.viewController.updateFloat());
        $(window).on('resize', () => this.viewController.updateFloat());

        this.setMapListeners();
        this.addTilesLoadedListener();
        this.addResizeListener();

        this.viewController.createArchiveToggle({
            parent: 'TopoMarker',
            labelText: this.globalObjectConnector.GetBasicJSTopoObject().str('ARCHIVES_MORE'),
            onToggle: (checked) => {
                mediator.publish("marker:archive:show", { checked: checked });
            }
        });

        if (initGallery) {
            this.switchTo(MARKER_MODE.GALLERY, this.globalObjectConnector.GetIndexJSGlobalResultObject());
        } else {
            this.viewController.updateFloat();
        }

        return this.getMapInstance();
    }

    setMapOptions(mode = 'main', customOpts = {}) {
        const opts = { ...(MAP_OPTIONS[mode] || MAP_OPTIONS.main), ...customOpts };
        if (this.hasValidMap()) {
            this.mapAdapter.setMapOptions(opts);
        } else {
            console.warn('[MapController] No map instance available when setting options.');
        }
    }

    setMapListeners() {
        this.mapListeners.set('heading_changed',
            this.mapAdapter.addListener(this.mapAdapter.getMapInstance(), 'heading_changed', () => {
                if (this.isMainActive()) {
                    this.documentMarkerManager.rotateMarkers();
                }
            })
        );
        this.mapListeners.set('projection_changed',
            this.mapAdapter.addListener(this.mapAdapter.getMapInstance(), 'projection_changed', () => {
                if (this.isMainActive()) {
                    this.documentMarkerManager.rotateMarkers();
                }
            })
        );
    }

    // --- Adapter-Pass-throughs ---

    getMapInstance() {
        return this.mapAdapter.getMapInstance();
    }

    getCurrentCanvas() {
        return this.mapAdapter.getMapDiv();
    }

    hasValidMap() {
        return !!this.mapAdapter.getMapInstance();
    }

    scheduleTimeout(name, delay, callback) {
        timeoutRegistry.schedule(name, delay, callback);
    }

    createOverlay() {
        return this.mapAdapter.getOverlayInstance();
    }

    removeOverlay() {
        this.mapAdapter.removeOverlay();
    }

    listenOverlayReady(callback) {
        const overlay = this.mapAdapter.getOverlayInstance();
        if (overlay) {
            this.mapAdapter.addListener(overlay, 'ready', callback);
        }
    }

    // --- Zustand, Marker, Clusterer ---

    setupClusterer(markerClustererLib) {
        this.markerClusterer = new MarkerClustererAdapter(markerClustererLib, this.mapAdapter.getMapInstance(), {
            render: this.documentMarkerManager.renderCustomClusterMarker
        });
        this.markerClusterer.initialize();
        this.documentMarkerManager.setMarkerClusterer(this.markerClusterer);

        // Cluster-Leak (Phase 7b): nach jedem Re-Clustering veraltete 'cluster'-Registry-Einträge freigeben
        // (Quelle der Wahrheit = aktuelle clusterer.clusters; siehe DocumentMarkerManager.pruneStaleClusters).
        this.mapAdapter.addListener(
            this.markerClusterer.getInstance(),
            'clusteringend',
            () => this.documentMarkerManager.pruneStaleClusters()
        );
    }

    isMainActive() {
        return this.mapPositions.isMain() && this.mapAdapter.isMapAttached();
    }

    isGalleryActive() {
        return this.mapPositions.isGallery() && this.mapAdapter.isMapAttached();
    }

    // mode-only (ohne Map-Attached-Prüfung) — ersetzt FM.Position-Vergleiche
    isMain() {
        return this.mapPositions.isMain();
    }

    isGallery() {
        return this.mapPositions.isGallery();
    }

    triggerResize() {
        this.mapAdapter.triggerResize();
    }

    fitBounds(bounds) {
        this.mapAdapter.fitBounds(bounds);
    }

    switchToGallery() {
        this.viewController.switchCanvasToGallery();
        this.mapPositions.set(MapPositions.GALLERY);
    }

    restoreMain() {
        this.viewController.restoreMainCanvas();
        this.mapPositions.set(MapPositions.MAIN);
    }

    switchTo(to, localRes) {
        if (!this.hasValidMap() || !this.getCurrentCanvas()) { return; }

        // mapType beim VERLASSEN des Noch-Kontexts sichern: `_currentContext()` ist hier noch der ALTE Kontext (vor
        // switchToGallery/restoreMain) → nicht-racy, kein Bleed zwischen den Kontexten. Apply-from-store erfolgt im Setup.
        const leavingMap = this.getMapInstance();
        if (leavingMap && leavingMap.getMapTypeId) {
            this._getMapStateStore(this._currentContext()).patch({ mapType: leavingMap.getMapTypeId() });
        }

        // OV-Panel ist ein Reconciler-Modul (`contexts:['main','gallery']`): beim View-/Positionswechsel invalidieren →
        // der folgende Reconcile baut es frisch auf. Das Handle.destroy macht den Teardown (Panel + Layer + getilte
        // Overlays + maptypeid_changed-Listener) = Reset für den Zielkontext → fixt den gallery→main-Leak
        // (visible/opacity bleiben nicht stehen) und ersetzt das frühere manuelle Schließen/Clearen.
        if (this._mapUiReconciler) { this._mapUiReconciler.invalidateModule('overlayPanel'); }
        // Karten-UI sofort auf den ZIEL-Kontext reconcilen (isGalleryActive() ist mid-switch noch alt) → Suchkreis main-only etc.
        this._reconcileMapUi(to === MARKER_MODE.GALLERY ? 'gallery' : 'main');

        if (to === MARKER_MODE.GALLERY && this.isMainActive()) {
            if (localRes) {
                localRes.lastZoom = this.mapAdapter.getZoom();
                localRes.lastPos = this.mapAdapter.getCenter();
            }

            const $TopoMarkerinput = $('#TopoMarker').find('input[type="checkbox"]:checked');
            if ($TopoMarkerinput[0]) {
                $TopoMarkerinput.trigger('click');
            }

            this.switchToGallery();

            // mapType apply-from-store EINMALIG beim Galerie-Eintritt (A): die geteilte Karte trägt noch den Main-Typ →
            // gemerkten Galerie-Typ wiederherstellen, sonst Satellit. Der Doku-Wechsel (#loadGalleryMarkers) fasst den Typ
            // danach NICHT mehr an — nur die Marker werden getauscht → Karte/Satellit + Variante bleiben.
            const enterGalleryMap = this.getMapInstance();
            const enterGalleryMapType = this._getMapStateStore('gallery').get().mapType
                || this.mapAdapter.performMapAction('getMapTypeConstant', { type: 'satellite' });
            if (enterGalleryMap && enterGalleryMap.setMapTypeId && enterGalleryMapType) {
                enterGalleryMap.setMapTypeId(enterGalleryMapType);
            }

            if (!this.openGalleryLegacy) {
                while (this.eventListeners.length) {
                    const el = this.eventListeners.pop();
                    this.mapAdapter.clearListeners(el);
                }
            }

            const area = this.getSearchArea();
            if (area && area.circle && area.circle.getCenter() && area.circle.getRadius() > 0) {
                area.circle.setCenter(null);
                area.circle.setRadius(null);
                area.circleCenter = null;
                area.circleRadius = null;
            }

            this.layoutConnector.GetResultContainer().trigger('scroll');

            this.scheduleTimeout('gallery/open', 200, () => {
                this.layoutConnector.GetGallerySidepanelContainer().trigger('resize');
            });

        } else if (to === MARKER_MODE.MAIN && this.isGalleryActive()) {
            const $GMCanvas = $(this.getCurrentCanvas());
            if ($GMCanvas.hasClass('ui-resizable')) {
                $GMCanvas.resizable('destroy');
            }

            this.restoreMain();

            const currentMapType = this.mapAdapter.getMapTypeId();

            const mapOptions = $.extend(this.mapOptions, {
                zoomControl: false,
                streetViewControl: false,
                panControl: true,
                rotateControl: false,   // Googles Control aus — custom Tilt/Rotate-Modul (tiltRotateControl.js) ersetzt es
                fullscreenControl: false,
                // mapType apply-from-store: gemerkten Main-Typ wiederherstellen, sonst der bisherige currentMapType.
                mapTypeId: this._getMapStateStore('main').get().mapType || currentMapType,
                labels: false
            });
            this.mapAdapter.setMapOptions(mapOptions);

            this.layoutConnector.GetResultContainer().trigger('scroll');

            if (localRes && localRes.lastZoom || localRes.lastPos) {
                this.scheduleTimeout('gallery/close/restoreLastZoomAndPos', 100, () => {
                    this.mapAdapter.setZoom(localRes.lastZoom);
                    this.mapAdapter.setCenter(localRes.lastPos);
                });
            }
        }
    }

    addDocumentMarkers(dataArray) {
        const normalized = dataArray.map(raw => this.#toDocumentMarkerData(raw));
        this.documentMarkerManager.addMarkers(normalized);
    }

    clearDocumentMarkers() {
        this.documentMarkerManager.clearMarkers();
    }

    highlightDocumentMarker(id, active = true) {
        this.documentMarkerManager.highlightMarkerContainingDocument(id, active);
    }

    addArchiveMarkers(dataArray) {
        this.archiveMarkerManager.addMarkers(dataArray);
    }

    clearArchiveMarkers() {
        this.archiveMarkerManager.clearMarkersByType('archive');
    }

    highlightArchiveMarker(id, active = true) {
        this.archiveMarkerManager.highlightMarkerById(id, active);
    }

    fitToAllMainMapMarkers() {
        const allMarkers = [
            ...this.documentMarkerManager.getAllVisibleMarkers()
        ];

        const markersWithPosition = allMarkers.filter((marker) => marker.position);
        this.mapAdapter.fitMapToMarkers(this.mapAdapter.getMapInstance(), markersWithPosition);
    }

    getAllDocumentMarkers() {
        return this.documentMarkerManager.getMarkersByType('document');
    }

    addGalleryMarkers({ locations, viewpoints, mapoptions }) {
        this.galleryMarkerManager.addMarkers(this.mapAdapter.getMapInstance(), { locations, viewpoints });
    }

    deleteGalleryMarkers() {
        this.galleryMarkerManager.clearAll();
    }

    fitToGalleryMapMarkers(index = null) {
        this.mapAdapter.fitMapToMarkers(this.mapAdapter.getMapInstance(), this.galleryMarkerManager.getMarkers(index));
    }

    clearMarkers(mode = MARKER_MODE.ALL) {
        if (!this.hasValidMap()) { return; }

        while (this.eventListeners.length) {
            const el = this.eventListeners.pop();
            this.mapAdapter.clearListeners(el);
        }

        this.clearDocumentMarkers();
        this.clearArchiveMarkers();

        if (this.layoutConnector.GetGalleryMapWrapper() && (mode === MARKER_MODE.ALL || mode === MARKER_MODE.GALLERY)) {
            this.globalObjectConnector.GetIndexJSGlobalGalleryResultObject().addMarkers = false;
            this.layoutConnector.GetGalleryMapWrapper().removeData('Bounds');
            this.deleteGalleryMarkers();
            this.removeOverlay();
        }
    }

    // --- Bootstrap-Wiring (Listener, Tiles, Events) ---

    subscribeLegacyEvents() {
        mediator.subscribe("marker:archive:show", ({ checked }) => {
            this.toggleArchiveMarkers(checked);
        });
        mediator.subscribe("circlesearch:trigger", () => {
            this.triggerAreaSearch();
        });
    }

    addResizeListener() {
        this.mapAdapter.addListener(this.getMapInstance(), 'resize', () => {
            this.scheduleTimeout("map/resize", 50, () => this.onResize());
        });
    }

    addTilesLoadedListener() {
        this.eventListeners.push(
            this.mapAdapter.addListener(this.getMapInstance(), 'tilesloaded', () => {
                this.onTilesLoaded();
            })
        );
    }

    addMapTypeIDChangedListener() {
        if (!this.eventListeners.find(checklistener, 'maptypeid_changed')) {
            this.eventListeners.push(this.mapAdapter.addListener(this.getMapInstance(), 'maptypeid_changed', () => {
                const map = this.getMapInstance();
                MapTypeChanged(map);
                // Store „ehrlich" halten (B): den live gewählten Typ in den Store des AKTIVEN Kontexts spiegeln, damit
                // apply-from-store (Galerie-Eintritt / Main-Rückkehr) die WAHRHEIT wiederherstellt statt eines Altwerts.
                if (map && map.getMapTypeId) {
                    this._getMapStateStore(this._currentContext()).patch({ mapType: map.getMapTypeId() });
                }
            }));
        }
    }

    onResize() {
        this.viewController.onResize();
        if (this.isMainActive() || this.isGalleryActive()) {
            this.refreshMarkers();
        }
    }

    onTilesLoaded() {
        if (this.eventListeners.length === 1 && this.getCurrentCanvas()) {
            $(document).on("pageshow", () => {
                const center = this.getMapInstance().getCenter();   // war frontendMap.getCenter() (existierte nicht) — korrigiert
                this.mapAdapter.trigger(this.getMapInstance(), "resize");
                this.getMapInstance().setCenter(center);
            });

            this.getMapInstance().draggable = true;

            // create button to fit map to markers
            $('#mainCenterButton').on('click', () => {
                mediator.publish('map:fit:main', {});
            });

            // In-Karten-Bedien-UI (Chef-Direktive): baut refreshMarkers() idempotent über _reconcileMapUi() (MAP_UI_CONFIG →
            // MapUiReconciler) auf → überlebt Gallery-Refresh/Tiles-Reload, die die Controls leeren, ohne Dubletten.

            // OV-Panel (Main) wird jetzt vom MapUiReconciler gebaut (overlayPanel-Modul, ovSide:'right') — kein
            // #overlayControls-Mount mehr. Galerie analog über den Reconciler (ovSide:'left').

            if (typeof Basemap !== 'undefined') {
                this.addMapTypeIDChangedListener();
                this.scheduleTimeout("map/mapTypeChanged", 30, () => MapTypeChanged(this.getMapInstance()));
            }
        }
        this.refreshMarkers();
    }

    // --- Marker-Orchestrierung ---

    refreshMarkers() {
        this._reconcileMapUi((this.isGalleryActive && this.isGalleryActive()) ? 'gallery' : 'main');
        if (this.isMainActive()) { this.#loadMainMarkers(); }
        if (this.isGalleryActive()) { this.#loadGalleryMarkers(); }
    }

    // Stellt die In-Karten-Bedien-UI deklarativ her (MAP_UI_CONFIG → MapUiReconciler): idempotent pro Karte/Kontext.
    // An den Lebenszyklus-Hooks aufgerufen (refreshMarkers = Live-Kontext, switchTo = Ziel-Kontext beim Wechsel);
    // überlebt Refresh/Tiles-Reload ohne Dubletten (Dedup über die Controls-MVCArray in MapToolsMenu).
    _reconcileMapUi(context) {
        if (!window.google || !this.hasValidMap()) { return; }
        if (!this._mapUiReconciler) { this._mapUiReconciler = createMapUiReconciler(window.google, this._mapUiApi()); }
        this._mapUiReconciler.reconcile(this.getMapInstance(), context);
    }

    // Schlanke Context-API für den MapUiReconciler (Slice 1): die Actions/Toggles aus MAP_UI_CONFIG laufen hierüber,
    // damit der Katalog reine Daten bleibt. isGallery() prüft LIVE (wie der bisherige „zentrieren"-Button beim Klick).
    // Per-Kontext-Store-Registry (Slice 4b). Lazy je Kontext (main/gallery/locate); eigener Store → kein Zustands-Bleed
    // zwischen den Kontexten. Debug-Handle: window.__topoMapStores (z.B. __topoMapStores.main.get()).
    _getMapStateStore(context) {
        const ctx = (context === 'gallery') ? 'gallery' : (context === 'locate' ? 'locate' : 'main');
        if (!this._mapStateStores) {
            this._mapStateStores = {};
            if (typeof window !== 'undefined') { window.__topoMapStores = this._mapStateStores; }
        }
        if (!this._mapStateStores[ctx]) {
            const st = new MapStateStore(createDefaultMapState(ctx));
            st.patch({ keepOnPositionChange: KEEP_SETTINGS_ON_SWITCH });
            this._mapStateStores[ctx] = st;
        }
        return this._mapStateStores[ctx];
    }

    _currentContext() {
        return (this.isGalleryActive && this.isGalleryActive()) ? 'gallery' : 'main';
    }

    _mapUiApi() {
        const self = this;
        if (!self._mapUiWired) {
            self._mapUiWired = true;
            self._getMapStateStore('main');   // initialer Store + Debug-Handle
            // Archiv-Sichtbarkeit (Button ODER versteckte #cb_topomarker-Checkbox) → write-through in den AKTUELLEN Kontext-Store.
            mediator.subscribe('marker:archive:show', ({ checked }) => self._getMapStateStore(self._currentContext()).patch({ archivesShown: !!checked }));
            // Chef-Wunsch (4): gezogener Suchkreis (radius>0) → Lösch-Button sichtbar; leerer Trigger (nur Klick) → aus.
            mediator.subscribe('circlesearch:trigger', ({ center, radius }) => self._getMapStateStore(self._currentContext()).patch({ searchCircleActive: !!(center && radius) }));
        }
        return {
            getStore: (context) => self._getMapStateStore(context),
            isGallery: () => !!(self.isGalleryActive && self.isGalleryActive()),
            publish: (event, payload) => mediator.publish(event, payload),
            subscribe: (event, cb) => mediator.subscribe(event, cb),
            setSearchCircleLeftClick: (active) => {
                self._getMapStateStore(self._currentContext()).patch({ searchCircleMode: !!active });   // write-through
                if (self.circleSearch && self.circleSearch.setLeftClickMode) { self.circleSearch.setLeftClickMode(active); }
            },
            // Chef-Wunsch (4): Suchkreis löschen — visuellen Kreis weg + wie der Rechtsklick-ohne-Drag den LEEREN Trigger feuern:
            // setzt die Suche zurück + holt Dokumente neu (triggerAreaSearch, Subscriber oben) UND blendet den Lösch-Button aus
            // (Store-Subscriber → searchCircleActive=false).
            clearSearchCircle: () => {
                if (self.circleSearch && self.circleSearch.deleteCircle) { self.circleSearch.deleteCircle(); }
                mediator.publish('circlesearch:trigger', { center: null, radius: null });
            },
            // Map-UI-Unify (Chef 16): OV-Panel auf/zu — das Katalog-OV-Item (mapTools/Galerie) löst es über das
            // overlayPanel-Modul-Handle aus (das auf die aktuelle Karten-Instanz zeigt; bei Kontextwechsel neu gebaut).
            toggleOverlayPanel: () => {
                const h = self._mapUiReconciler && self._mapUiReconciler.getModule('overlayPanel');
                if (h && h.toggle) { h.toggle(); }
            },
            // {id}-Doc-Link im Overlay-Infotext (Frontend) → Gallery öffnen: die injizierte Legacy-openGallery
            // (setOpenGalleryHandler), NICHT global. Im Admin-Kontext nicht gesetzt → das overlayPanel-Modul lässt den Klick no-op.
            openGallery: (did) => { if (self.openGalleryLegacy) { self.openGalleryLegacy(did); } },
            // Galerie-Hooks für das multiLocation-Modul (gleiche Quellen wie der bisherige ViewController-Aufbau).
            getGalleryMarkers: () => (self.galleryMarkerManager ? self.galleryMarkerManager.getMarkers() : null),
            fitGalleryToIndex: (index) => self.fitToGalleryMapMarkers(index),
            allLabel: () => self.globalObjectConnector.GetBasicJSTopoObject().str('ALL')
        };
    }

    toggleArchiveMarkers(show = true) {
        if (show) {
            const archivesData = $.parseJSON($.base64.decode(this.globalObjectConnector.GetHeadPHPOtherArchivesObject()));
            this.addArchiveMarkers(archivesData);
        } else {
            this.clearArchiveMarkers();
        }
    }

    #loadMainMarkers() {
        this.drawAreaCircle(this.globalObjectConnector.GetIndexJSGlobalResultObject().area);
        this.initDummyOverlay();

        const wrapper = this.layoutConnector.GetMapContainerWrapper()[0];
        if (wrapper.getAttribute('addMarkers')) {
            this.clearMarkers(MARKER_MODE.MAIN);
            // marker-fields: [0]=docId [1]=lat [2]=lng [3]=type_id [4]=category_id [5]=alpha [6]=0
            const markers = JSON.parse(wrapper.getAttribute('Markers'));
            this.addDocumentMarkers(markers);
            wrapper.removeAttribute('addMarkers');
        }
    }

    #loadGalleryMarkers() {
        const galleryResultObject = this.globalObjectConnector.GetIndexJSGlobalGalleryResultObject();
        if (!galleryResultObject || galleryResultObject.addMarkers !== true) { return; }

        const mapOptions = {
            locations: galleryResultObject.locations,
            viewpoints: galleryResultObject.viewpoints,
            mapOptions: {
                noClear: true,
                zoomControlOptions: { ZoomControlStyle: 'SMALL' },
                // HINWEIS: dieses mapOptions-Objekt wird von addGalleryMarkers IGNORIERT (totes Plumbing). Der Map-Typ wird
                // unten EXPLIZIT gesetzt (apply-from-store). mapTypeId hier nur noch als Doku des Default-Intents.
                mapTypeId: this.mapAdapter.performMapAction('getMapTypeConstant', { type: 'satellite' }),
                panControl: true,
                rotateControl: false   // (totes Plumbing, der Vollständigkeit halber) — custom Tilt/Rotate-Modul ersetzt Googles
            }
        };

        this.clearMarkers();

        galleryResultObject.addMarkers = false;

        let viewpoints;
        if (galleryResultObject.viewpoints) {
            viewpoints = [{
                location_id: galleryResultObject.id,
                name: null,
                position: 0,
                markers: galleryResultObject.viewpoints
            }];
        }

        this.addGalleryMarkers({
            locations: galleryResultObject.locations,
            viewpoints: viewpoints,
            mapoptions: mapOptions
        });
        // mapType wird hier NICHT mehr gesetzt (A): das kippte den Typ bei JEDEM Doku-Wechsel auf den Store-Default zurück.
        // apply-from-store passiert jetzt einmalig beim Galerie-Eintritt (switchTo → gallery); der Store bleibt via
        // maptypeid_changed live (B, Wahrheit). Beim Doku-Wechsel werden nur die Marker getauscht.

        this.listenOverlayReady(() => {
            // Galerie-Marker frisch & bereit → multiLocation mit aktuellen Markern neu aufbauen (über den Reconciler).
            if (this._mapUiReconciler) { this._mapUiReconciler.invalidateModule('multiLocation'); }
            this._reconcileMapUi((this.isGalleryActive && this.isGalleryActive()) ? 'gallery' : 'main');
        });
        // OV-Panel (Galerie) baut jetzt der MapUiReconciler (overlayPanel-Modul, ovSide:'left') — createGalleryOverlayControls entfällt.
        // Galerie-Center entflechten (Schritt 1): initiales Auto-Fit direkt per Event statt über `.gallery_map_center`.click()
        // — der manuelle Center läuft über den MapToolsMenu-center. Der Legacy-Button bleibt vorerst (Schritt 2/3).
        mediator.publish('map:fit:gallery', { index: null });

        this.globalObjectConnector.CallBasicJSHandleMaxZoom(this.getMapInstance());
    }

    // --- Kreissuche / Area ---

    getSearchArea() {
        return { circle: this.circleSearch.getCircle() };
    }

    initDummyOverlay() {
        this.overlay = this.createOverlay();
    }

    drawAreaCircle(areaData) {
        if (!areaData || typeof areaData.center === 'undefined') { return; }
        const area = this.getSearchArea();
        if (area && area.circle) {
            area.circle.setCenter(this.mapAdapter.createLatLng(areaData.center.y, areaData.center.x));
            area.circle.setRadius(Math.round(areaData.radius));
        }
    }

    deleteCircle() {
        const area = this.getSearchArea();
        if (!area || !area.circle) { return; }

        console.debug('[MapController]: deleteCircle() called!');
        this.scheduleTimeout("map/areasearch/delete", 300, () => {
            this.circleSearch.deleteCircle();
            this.mapAdapter.trigger(this.getMapInstance(), 'mouseup');
        });
    }

    triggerAreaSearch() {
        // ToDo (Decoupling): statt direkt DoSearch zu rufen, sollten hier die Flächensuche-Daten
        // über den Mediator publiziert werden; das Such-Modul subscribt extern. Vorerst verhaltensgleich.
        this.scheduleTimeout("map/areasearch/trigger", 200, () => {
            let searchTerm = this.globalObjectConnector.GetIndexJSSearchString();
            searchTerm = searchTerm.replace(/\[area:.*?]/g, '');

            const area = this.getSearchArea();
            if (area && area.circle && area.circle.getCenter() && area.circle.getRadius() > 0) {
                this.globalObjectConnector.GetIndexJSBackgroundSearchTerms().area =
                    '[area:' + area.circle.getCenter().lat() + '|' + area.circle.getCenter().lng() + ';' + area.circle.getRadius() + ']';
            } else {
                delete this.globalObjectConnector.GetIndexJSBackgroundSearchTerms().area;
            }
            this.globalObjectConnector.CallIndexJSSetSearchterm(searchTerm);
            this.globalObjectConnector.GetIndexJSDoSearch('hideMenu');
        });
    }

    #toDocumentMarkerData(raw) {
        if (typeof raw.id !== 'undefined') { return raw; }

        return {
            id: raw[0],
            lat: raw[1],
            lng: raw[2],
            typeId: raw[3],
            categoryId: raw[4],
            rotation: raw[5],
            position: raw[6]
        };
    }
}
